import React, { useRef } from "react";
import { useSite } from "../context/SiteContext";

/**
 * Tap-to-edit text. In admin Edit Mode the element becomes editable inline;
 * for everyone else it renders the saved override (or the code default).
 * Usage: <Editable id="home.hero.title" as="h1" className="...">Default text</Editable>
 */
export function Editable({ id, as: Tag = "span", className = "", children }) {
  const { content, editMode, isAdmin, saveText } = useSite();
  const def = typeof children === "string" ? children : String(children ?? "");
  const text = content[id] != null ? content[id] : def;
  const editable = editMode && isAdmin;
  const ref = useRef(null);

  const commit = () => {
    if (!ref.current) return;
    const v = (ref.current.innerText || "").trim();
    if (v !== text) saveText(id, v, def);
  };

  return (
    <Tag
      ref={ref}
      key={editable ? "edit" : "view"}
      data-editkey={id}
      data-testid={`editable-${id}`}
      className={`${className}${editable ? " uptoo-editable" : ""}`}
      contentEditable={editable}
      suppressContentEditableWarning
      spellCheck={false}
      onBlur={editable ? commit : undefined}
      onKeyDown={
        editable
          ? (e) => {
              if (e.key === "Enter" && Tag !== "p" && Tag !== "textarea") {
                e.preventDefault();
                e.currentTarget.blur();
              }
              if (e.key === "Escape") e.currentTarget.blur();
            }
          : undefined
      }
      title={editable ? "Click to edit this text" : undefined}
    >
      {text}
    </Tag>
  );
}

export default Editable;
